import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc,
  setDoc,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import { 
  FaSearch, 
  FaMapMarkerAlt, 
  FaBriefcase,
  FaClock,
  FaCheckCircle,
  FaArrowRight,
  FaTimes,
  FaMoneyBillWave,
  FaUser,
  FaCalendarAlt,
  FaBookmark,
  FaRegBookmark,
  FaBuilding
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Spinner from '../components/Spinner';
import { db } from '../firebase';

const ProfessionalDashboard = () => {
  // State management
  const [jobs, setJobs] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  const [activeTab, setActiveTab] = useState('search'); // 'search' or 'applications'
  const [applyingJob, setApplyingJob] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          } else {
            // Create a basic profile from auth data if no profile exists
            const basicProfile = {
              displayName: user.displayName || 'User',
              email: user.email,
              createdAt: serverTimestamp()
            };
            await setDoc(doc(db, 'users', user.uid), basicProfile);
            setUserProfile(basicProfile);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };

    fetchUserProfile();
  }, [user]);

  // Fetch applied jobs on component mount
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    const fetchData = async () => {
      try {
        await fetchAppliedJobs();
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data. Please try again.');
      }
    };
    
    fetchData();
  }, [user, navigate]);
  
  // Search jobs (client-side filtering)
  const handleSearch = async () => {
    try {
      setLoading(true);
      console.log('Filtering jobs with:', { searchTerm, location });
      
      // Fetch all jobs first (if not already loaded)
      if (jobs.length === 0) {
        console.log('No jobs in state, fetching...');
        await fetchAllJobs();
      }
      
      // The actual filtering is handled by the filteredJobs computed value
      console.log('Filtered jobs count:', filteredJobs.length);
      return filteredJobs;
    } catch (error) {
      console.error('Error searching jobs:', error);
      toast.error('Failed to search jobs. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Initial fetch of all jobs
  const fetchAllJobs = async () => {
    try {
      console.log('Fetching all jobs...');
      const jobsRef = collection(db, 'jobPosts');
      console.log('Collection path:', jobsRef.path);
      
      // First, check if collection exists and get count
      try {
        const colSnapshot = await getCountFromServer(jobsRef);
        console.log('Collection exists with count:', colSnapshot.data().count, 'documents');
      } catch (error) {
        console.error('Error getting collection count:', error);
      }
      
      // Try a simple query without any filters first
      console.log('Executing query without filters...');
      const q = query(jobsRef);
      const querySnapshot = await getDocs(q);
      
      console.log('Query completed. Document count:', querySnapshot.size);
      
      const jobsData = [];
      let docCount = 0;
      
      querySnapshot.forEach((doc) => {
        console.log(`Job ${++docCount}:`, doc.id, doc.data().title || 'No title');
        jobsData.push({ id: doc.id, ...doc.data() });
      });
      
      console.log(`Processed ${docCount} jobs`);
      
      if (docCount === 0) {
        console.warn('No jobs found in the jobPosts collection with simple query');
        
        // Try with orderBy to see if that's the issue
        try {
          console.log('Trying query with orderBy...');
          const orderedQ = query(jobsRef, orderBy('createdAt'));
          const orderedSnapshot = await getDocs(orderedQ);
          console.log('Ordered query count:', orderedSnapshot.size);
        } catch (orderError) {
          console.error('Error with ordered query:', orderError);
        }
      }
      
      setJobs(jobsData);
      return jobsData;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs. Please refresh the page.');
      throw error;
    }
  };

  // Fetch jobs on component mount and when search criteria change
  useEffect(() => {
    const loadJobs = async () => {
      try {
        setLoading(true);
        await fetchAllJobs();
      } catch (error) {
        console.error('Error loading jobs:', error);
        toast.error('Failed to load jobs. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadJobs();
  }, []);
  
  // Fetch jobs on component mount
  useEffect(() => {
    const loadJobs = async () => {
      try {
        setLoading(true);
        await fetchAllJobs();
      } catch (error) {
        console.error('Error loading jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadJobs();
  }, []);

  // Fetch user's applied jobs
  const fetchAppliedJobs = useCallback(async () => {
    if (!user) return;
    
    try {
      const applicationsRef = collection(db, 'applications');
      const q = query(
        applicationsRef,
        where('applicantId', '==', user.uid)
      );
      
      const querySnapshot = await getDocs(q);
      const appliedJobsData = [];
      
      querySnapshot.forEach((doc) => {
        appliedJobsData.push(doc.data().jobId);
      });
      
      setAppliedJobs(appliedJobsData);
    } catch (error) {
      console.error('Error fetching applied jobs:', error);
      throw error;
    }
  }, [user]);
  
  // Handle job application
  const handleApplyJob = async (jobId, jobTitle) => {
    if (!user) {
      toast.info('Please sign in to apply for jobs');
      navigate('/login', { state: { from: '/professional-dashboard' } });
      return;
    }

    try {
      setApplyingJob(jobId);
      
      // Check if already applied
      if (appliedJobs.includes(jobId)) {
        toast.info('You have already applied to this job');
        return;
      }

      const applicationId = `${user.uid}_${jobId}`;
      const applicationData = {
        jobId,
        jobTitle,
        applicantId: user.uid,
        applicantName: user.displayName || 'Anonymous',
        status: 'applied',
        appliedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        notificationStatus: 'pending' // Track notification status
      };

      // Save application to Firestore
      await setDoc(doc(db, 'applications', applicationId), applicationData);
      
      // Update local state
      setAppliedJobs(prev => [...prev, jobId]);
      
      // Show success message
      toast.success('Application submitted successfully!');
      
      // The Cloud Function will automatically send the email notification
      // when the application document is created
      
    } catch (error) {
      console.error('Error applying to job:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setApplyingJob(null);
    }
  };
  
  // Helper function to get location string from job
  const getLocationString = (location) => {
    if (!location) return 'Remote';
    if (typeof location === 'string') return location;
    // Handle location object
    return [location.city, location.area, location.state, location.country]
      .filter(Boolean)
      .join(', ') || 'Remote';
  };

  // Filter jobs based on search - no permission checks, just simple matching
  const filteredJobs = jobs.filter(job => {
    const searchLower = searchTerm.toLowerCase();
    const locationLower = location.toLowerCase();
    
    const matchesSearch = !searchTerm || 
                         (job.title?.toLowerCase().includes(searchLower) || 
                          job.description?.toLowerCase().includes(searchLower) ||
                          job.company?.toLowerCase().includes(searchLower));
                          
    const jobLocation = getLocationString(job.location);
    const matchesLocation = !location || 
                          (jobLocation.toLowerCase().includes(locationLower));
    
    return matchesSearch && matchesLocation;
  });
  
  // Get applied jobs details
  const appliedJobsDetails = jobs.filter(job => appliedJobs.includes(job.id));

  // Format location
  const formatLocation = (location) => {
    if (!location) return 'Remote';
    if (typeof location === 'string') return location;
    // Handle location object
    return [location.city, location.area, location.state, location.country]
      .filter(Boolean)
      .join(', ') || 'Remote';
  };

  // Format salary
  const formatSalary = (job) => {
    if (job.salary) return job.salary;
    if (job.salaryMin || job.salaryMax) {
      return `${job.salaryMin ? `₹${job.salaryMin.toLocaleString()}` : ''}${job.salaryMin && job.salaryMax ? ' - ' : ''}${job.salaryMax ? `₹${job.salaryMax.toLocaleString()}` : ''}${job.salaryMin || job.salaryMax ? '/month' : ''}`;
    }
    return 'Salary not specified';
  };

  // Format experience
  const formatExperience = (job) => {
    if (job.experienceMin || job.experienceMax) {
      return `${job.experienceMin || 0} - ${job.experienceMax || '∞'} years`;
    }
    return 'Experience not specified';
  };

  // Handle view job details
  const handleViewJob = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };

  // Save job
  const handleSaveJob = async (jobId) => {
    if (!user) {
      toast.info('Please sign in to save jobs');
      return;
    }
    
    setIsSaving(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const savedJobs = userDoc.data()?.savedJobs || [];
      
      if (savedJobs.includes(jobId)) {
        await updateDoc(userRef, {
          savedJobs: arrayRemove(jobId)
        });
        toast.success('Job removed from saved jobs');
      } else {
        await updateDoc(userRef, {
          savedJobs: arrayUnion(jobId)
        });
        toast.success('Job saved successfully');
      }
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to save job');
    } finally {
      setIsSaving(false);
    }
  };

  // Format date for display
  const formatJobDate = (date) => {
    if (!date) return 'Recently';
    
    // If date is a Firestore timestamp, convert to Date
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    
    // If we still don't have a valid date, return a fallback
    if (isNaN(dateObj.getTime())) return 'Recently';
    
    const now = new Date();
    const diffInDays = Math.floor((now - dateObj) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Render job card
  const renderJobCard = (job) => {
    const isApplied = appliedJobs.includes(job.id);
    const isApplying = applyingJob === job.id;
    
    return (
      <div key={job.id} className={`bg-white rounded-lg shadow-md p-6 mb-4 border-l-4 ${isApplied ? 'border-gray-300 opacity-75' : 'border-indigo-500'}`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-semibold text-gray-800">{job.title}</h3>
          <span className="text-sm text-gray-500 whitespace-nowrap ml-2">
            {formatJobDate(job.createdAt || job.timestamp || job.postedAt)}
          </span>
        </div>
        
        <div className="flex items-center text-sm text-gray-600 mb-3">
          <FaBriefcase className="mr-1" />
          <span className="mr-4">{job.company || 'Not specified'}</span>
          <FaMapMarkerAlt className="mr-1" />
          <span>{formatLocation(job.location)}</span>
        </div>
        
        <p className="text-gray-700 mb-4 line-clamp-2">
          {job.description}
        </p>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            {isApplied ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <FaCheckCircle className="mr-1" /> Applied
              </span>
            ) : (
              <button
                onClick={() => handleApplyJob(job.id, job.title)}
                disabled={isApplying}
                className={`px-4 py-2 rounded-md text-sm font-medium text-white ${isApplying ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                {isApplying ? 'Applying...' : 'Apply Now'}
              </button>
            )}
          </div>
          <button 
            onClick={() => handleViewJob(job)}
            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
          >
            View Details <FaArrowRight className="ml-1" size={12} />
          </button>
        </div>
      </div>
    );
  };

  // Format work location
  const formatWorkLocation = (workLocation) => {
    if (!workLocation || workLocation.length === 0) return 'Not specified';
    return workLocation.join(', ');
  };

  // Format poster type
  const formatPosterType = (type) => {
    if (!type) return '';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Job Details Modal
  const JobDetailsModal = ({ job, onClose, onApply, isApplied, isApplying }) => {
    const [isClosing, setIsClosing] = React.useState(false);
    const modalRef = React.useRef(null);
    
    if (!job) return null;
    
    const posterType = formatPosterType(job.posterType);
    const workLocation = formatWorkLocation(job.workLocation);
    
    const handleClose = () => {
      setIsClosing(true);
      setTimeout(() => {
        onClose();
        setTimeout(() => setIsClosing(false), 300);
      }, 300);
    };
    
    React.useEffect(() => {
      const handleEscape = (e) => e.key === 'Escape' && handleClose();
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }, []);
    
    return (
      <div className="fixed inset-0 z-[9999] overflow-y-auto">
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
          aria-hidden="true"
          onClick={handleClose}
        >
          <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm"></div>
        </div>
        
        {/* Modal Panel */}
        <div className="flex justify-center items-start p-4 min-h-screen w-full">
          <div 
            ref={modalRef}
            className={`relative w-full max-w-4xl bg-white rounded-xl shadow-2xl transform transition-all duration-300 ease-in-out overflow-hidden ${
              isClosing ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-4 sm:px-6 relative">
              <div className="flex items-center">
                <button
                  onClick={handleClose}
                  className="mr-3 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors flex-shrink-0"
                  aria-label="Back to jobs"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                      {posterType} Posting
                    </span>
                    {job.type && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                        {job.type}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white truncate">{job.title}</h3>
                  <div className="flex items-center text-blue-100 text-sm mt-1">
                    <FaMapMarkerAlt className="mr-1.5 flex-shrink-0" />
                    <span className="truncate">{formatLocation(job.location)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Main Content */}
                  <div className="lg:col-span-2">
                    {/* Job Description */}
                    <div className="mb-6">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 pb-2 border-b">Job Description</h4>
                      <div className="prose max-w-none text-gray-700 space-y-3 text-sm sm:text-base">
                        <p className="text-gray-700 leading-relaxed">{job.description}</p>
                      </div>
                    </div>

                    {/* Requirements */}
                    {job.skills?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 pb-2 border-b">Required Skills</h4>
                        <div className="flex flex-wrap gap-2">
                          {job.skills.map((skill, index) => (
                            <span 
                              key={index}
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-50 text-blue-700"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sidebar */}
                  <div className="lg:col-span-1">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="text-base font-semibold text-gray-900 mb-3 pb-2 border-b">Job Overview</h4>
                      
                      <div className="space-y-3">
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Posted On</h5>
                          <p className="text-gray-900 text-sm">{formatDate(job.postedAt)}</p>
                        </div>
                        
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Location</h5>
                          <p className="text-gray-900 text-sm">{formatLocation(job.location)}</p>
                          {workLocation && (
                            <p className="text-xs text-gray-600 mt-1">Work: {workLocation}</p>
                          )}
                        </div>
                        
                        {job.type && (
                          <div>
                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Job Type</h5>
                            <p className="text-gray-900 text-sm">{job.type}</p>
                          </div>
                        )}
                        
                        {job.role && (
                          <div>
                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Role</h5>
                            <p className="text-gray-900 text-sm">{job.role}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-center sm:text-left">
                  <p className="text-xs sm:text-sm text-gray-600">
                    Posted by {posterType} • {formatDate(job.postedAt, true)}
                  </p>
                </div>
                <div className="flex justify-end">
                  {!isApplied ? (
                    <button
                      type="button"
                      onClick={() => onApply(job.id, job.title)}
                      disabled={isApplying}
                      className={`w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white shadow-sm ${
                        isApplying ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                    >
                      {isApplying ? 'Applying...' : 'Apply Now'}
                    </button>
                  ) : (
                    <span className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium bg-green-100 text-green-800">
                      <FaCheckCircle className="mr-2" /> Applied
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render applied job item
  const renderAppliedJob = (job) => (
    <div 
      key={job.id} 
      className="bg-white rounded-lg shadow-md p-4 mb-3 border-l-4 border-green-500 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => handleViewJob(job)}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <h4 className="font-medium text-gray-800">{job.title}</h4>
            <span className="text-sm text-green-600 flex items-center ml-2">
              <FaCheckCircle className="mr-1" /> Applied
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {job.company} • {getLocationString(job.location) || 'Remote'}
          </p>
          {job.postedAt && (
            <p className="text-xs text-gray-500 mt-1">
              Applied on {formatDate(job.postedAt)}
            </p>
          )}
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <button 
          onClick={(e) => {
            e.stopPropagation();
            handleViewJob(job);
          }}
          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
        >
          View Details <FaArrowRight className="ml-1" size={12} />
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {isModalOpen && selectedJob && (
          <JobDetailsModal
            job={selectedJob}
            onClose={closeModal}
            onApply={handleApplyJob}
            onSave={handleSaveJob}
            isApplied={appliedJobs.includes(selectedJob.id)}
            isApplying={applyingJob === selectedJob.id}
            isSaving={isSaving}
          />
        )}
        <div className="mb-8">
          <div className="flex flex-col space-y-1 mb-6">
            {userProfile?.displayName && (
              <span className="text-sm font-medium text-indigo-600 tracking-wide">
                Welcome back, {userProfile.displayName.split(' ')[0]}!
              </span>
            )}
            <p className="text-gray-500 text-sm font-light mb-4">
              Find and apply to jobs that match your expertise
            </p>
            
            {/* Search Form */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                    Search Jobs
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="search"
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md h-10 border p-2"
                      placeholder="Job title, keywords, or company"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaMapMarkerAlt className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="location"
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md h-10 border p-2"
                      placeholder="City, state, or remote"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 h-10 w-full justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Searching...
                      </>
                    ) : (
                      <>
                        <FaSearch className="mr-2" />
                        Search
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('search')}
              className={`${activeTab === 'search' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Available Jobs
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`${activeTab === 'applications' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              My Applications ({appliedJobs.length})
            </button>
          </nav>
        </div>
        
        {activeTab === 'search' ? (
          <>

            
            {/* Job Listings */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900">
                  {filteredJobs.length} {filteredJobs.length === 1 ? 'Job' : 'Jobs'} Found
                </h2>
                <div className="flex items-center text-sm text-gray-500">
                  <FaClock className="mr-1" />
                  <span>Updated just now</span>
                </div>
              </div>
              
              {filteredJobs.length > 0 ? (
                <div className="space-y-4">
                  {filteredJobs.map(renderJobCard)}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                  <FaSearch className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try adjusting your search or filter to find what you're looking for.
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">My Applications</h2>
            
            {appliedJobsDetails.length > 0 ? (
              <div className="space-y-3">
                {appliedJobsDetails.map(renderAppliedJob)}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                <FaBriefcase className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No applications yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Apply to jobs to see them here.
                </p>
                <button
                  onClick={() => setActiveTab('search')}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Browse Jobs
                </button>
              </div>
            )}
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default ProfessionalDashboard;
